/**
 * Design System Yoco - Styles CSS globaux
 * Inclut les styles mobile-first et safe areas
 */

import { GlobalStyles as MuiGlobalStyles } from '@mui/material';
import { colors, grey, fontFamily, mobile } from './tokens';

const GlobalStyles = () => (
  <MuiGlobalStyles
    styles={{
      // Reset et base
      '*, *::before, *::after': {
        boxSizing: 'border-box',
      },

      html: {
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        scrollBehavior: 'smooth',
        // Mobile: prevent zoom on input focus (iOS)
        touchAction: 'manipulation',
        // IMPORTANT: Prevent horizontal overflow at root level
        overflowX: 'hidden',
        width: '100%',
      },

      body: {
        margin: 0,
        padding: 0,
        fontFamily: fontFamily.body,
        backgroundColor: colors.bgPage,
        color: colors.textPrimary,
        lineHeight: 1.5,
        // Prevent overscroll bounce on iOS
        overscrollBehavior: 'none',
        // Mobile: prevent horizontal overflow
        overflowX: 'hidden',
        // NOTE: Safe areas gerees par les layouts (CoachLayout, AdminLayout)
        // pour eviter le double padding
      },

      // Focus visible pour accessibilite
      ':focus-visible': {
        outline: `2px solid ${colors.brand}`,
        outlineOffset: '2px',
      },

      // Liens
      a: {
        color: colors.brand,
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      },

      // Images responsive
      img: {
        maxWidth: '100%',
        height: 'auto',
      },

      // Scrollbar custom (WebKit)
      '::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '::-webkit-scrollbar-track': {
        background: colors.bgMuted,
      },
      '::-webkit-scrollbar-thumb': {
        background: grey[400],
        borderRadius: '4px',
        '&:hover': {
          background: grey[500],
        },
      },

      // Selection
      '::selection': {
        backgroundColor: colors.brand,
        color: colors.textInverse,
      },

      // ==================== MOBILE STYLES ====================

      // Mobile: disable text selection on buttons/nav (app-like feel)
      '@media (max-width: 600px)': {
        'button, a, .MuiBottomNavigation-root, .MuiListItemButton-root': {
          WebkitTapHighlightColor: 'transparent',
          userSelect: 'none',
        },
        // Mobile: smooth momentum scrolling
        '.MuiTableContainer-root, .MuiDialog-paper, .MuiDrawer-paper': {
          WebkitOverflowScrolling: 'touch',
        },
        // Mobile: larger touch targets for interactive elements
        'input, select, textarea, button': {
          fontSize: '16px !important', // Prevents zoom on iOS
        },
      },

      // Mobile: Hide scrollbars on mobile (cleaner look)
      '@media (max-width: 600px)': {
        '::-webkit-scrollbar': {
          display: 'none',
        },
        '*': {
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        },
      },

      // Safe area padding utilities
      '.safe-area-top': {
        paddingTop: mobile.safeAreaTop,
      },
      '.safe-area-bottom': {
        paddingBottom: mobile.safeAreaBottom,
      },
      '.safe-area-inset': {
        paddingTop: mobile.safeAreaTop,
        paddingBottom: mobile.safeAreaBottom,
        paddingLeft: mobile.safeAreaLeft,
        paddingRight: mobile.safeAreaRight,
      },

      // ==================== MOBILE LIST CONTAINER ====================
      // Classe utilitaire pour les conteneurs de listes sur mobile
      // Supprime les bordures et fond pour eviter l'effet "bloc dans bloc"
      '@media (max-width: 600px)': {
        '.mobile-list-container': {
          backgroundColor: 'transparent',
          border: 'none',
          borderRadius: 0,
          boxShadow: 'none',
          // Supprime le padding de CardContent quand utilise avec MuiCard
          '& .MuiCardContent-root': {
            padding: 0,
            '&:last-child': {
              paddingBottom: 0,
            },
          },
        },
        // Version avec juste le header visible (pour les sections avec titre)
        '.mobile-list-container-with-header': {
          backgroundColor: 'transparent',
          border: 'none',
          borderRadius: 0,
          boxShadow: 'none',
          '& .MuiCardContent-root': {
            padding: 0,
            '&:last-child': {
              paddingBottom: 0,
            },
          },
          // Garde le header avec un style subtil
          '& > [class*="MuiBox-root"]:first-of-type': {
            borderBottom: `1px solid ${colors.border}`,
            marginBottom: '12px',
          },
        },
      },
    }}
  />
);

export default GlobalStyles;
